/*
 * Hero parallax.
 *
 * Two planes cut from the same frame - the world painted in behind the subject,
 * and the near field (subject, pillar, foliage) - moved against each other as
 * the page scrolls. Scroll is the only input.
 *
 * Note on history: this file used to read window.scrollY, which is permanently
 * 0 in this theme because the page scrolls inside .page-wrapper rather than the
 * document. It therefore did nothing on every page load until now.
 */
(() => {
  /* Where scroll-driven animations exist, namaslay-brand.css drives both
     planes from a view timeline on the compositor. Step aside so the JS does
     not fight it - the animation would win the cascade regardless, but there
     is no reason to keep paying for the listener and style writes. */
  if (window.CSS && CSS.supports && CSS.supports('animation-timeline: view()')) return;

  const hero = document.querySelector('.namaref .nrhero');
  if (!hero || hero.dataset.nsDepth) return;
  hero.dataset.nsDepth = 'true';

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Which element scrolls depends on viewport width here: .page-wrapper on
     desktop, the document on narrow screens. Resolving it once at load is a
     race - images and fonts settle after this runs, and a wrapper that is not
     yet taller than its box reads as unscrollable, pinning the listener to the
     wrong element permanently. So: listen to both, and read whichever is
     actually moving. */
  const wrap = document.querySelector('.page-wrapper');
  const scrollTop = () =>
    Math.max(
      wrap ? wrap.scrollTop : 0,
      window.scrollY || 0,
      document.documentElement.scrollTop || 0,
      document.body.scrollTop || 0
    );

  /* Tunables. NEAR travels up as you scroll; FAR drifts the other way and less
     far, so the gap between them opens as you descend. PUSH is a slow scale on
     the far plane - the world receding rather than merely sliding, which is
     what separates this from a flat background-position trick. */
  const NEAR_THROW = 84;
  const FAR_RATIO = -0.55;
  const FAR_PUSH = 0.09;

  let frame = null;

  /* The hero's height only changes on resize, but getBoundingClientRect inside
     render() forced a synchronous layout on every animation frame during
     scroll. Measure once, re-measure on resize. */
  let span = 1;
  const measure = () => {
    span = Math.max(hero.getBoundingClientRect().height * 0.9, 1);
  };
  measure();

  const clear = () => {
    hero.style.removeProperty('--nr-depth-y');
    hero.style.removeProperty('--nr-far-y');
    hero.style.removeProperty('--nr-far-scale');
  };

  const render = () => {
    frame = null;
    if (reduced.matches) return clear();

    const progress = Math.min(1, Math.max(0, scrollTop() / span));
    // Ease out, so most of the movement happens early while the hero is still
    // the thing being looked at, rather than accumulating after it has gone.
    const eased = 1 - Math.pow(1 - progress, 2);

    // Narrow screens get a smaller throw: the hero is proportionally taller
    // there, so identical pixels read as a much larger movement.
    const scale = window.innerWidth < 750 ? 0.6 : 1;

    hero.style.setProperty('--nr-depth-y', (eased * NEAR_THROW * scale).toFixed(2) + 'px');
    hero.style.setProperty('--nr-far-y', (eased * NEAR_THROW * FAR_RATIO * scale).toFixed(2) + 'px');
    hero.style.setProperty('--nr-far-scale', (1 + eased * FAR_PUSH).toFixed(4));
  };

  const schedule = () => {
    if (frame === null) frame = requestAnimationFrame(render);
  };

  window.addEventListener('scroll', schedule, { passive: true });
  if (wrap) wrap.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener(
    'resize',
    () => {
      measure();
      schedule();
    },
    { passive: true }
  );

  const onPreferenceChange = () => (reduced.matches ? clear() : schedule());
  reduced.addEventListener
    ? reduced.addEventListener('change', onPreferenceChange)
    : reduced.addListener(onPreferenceChange);

  schedule();
})();
